import React, { useEffect, useState, useCallback } from "react";
import {
	Box,
	Typography,
	Button,
	Grid,
	CircularProgress,
	Alert,
	Link,
	Divider,
	IconButton,
} from "@mui/material";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { useNavigate, useParams } from "react-router-dom";
import { useAuth } from "../../../contexts/AuthContext";
import { useGrade } from "../../../contexts/GradeContext";
import {
	CARD_SURFACE,
	TOPIC_TILE_PALETTE,
} from "../../../components/teacher/module/teacherModuleStyles";
import { getTopicVisual } from "../../../components/teacher/module/topicVisuals";

/** Games (course categories) for a grade filtered by curriculum topic. */
const TeacherGamesByTopic = () => {
	const { gradeId, topicId } = useParams();
	const { user, supabase } = useAuth();
	const { updateSelectedGrade } = useGrade();
	const navigate = useNavigate();

	const [loading, setLoading] = useState(true);
	const [error, setError] = useState(null);
	const [gradeName, setGradeName] = useState("");
	const [topicLabel, setTopicLabel] = useState("");
	const [categories, setCategories] = useState([]);

	const load = useCallback(async () => {
		if (!user?.id || !gradeId || !topicId) return;
		setLoading(true);
		setError(null);
		try {
			const { data: allowed, error: aErr } = await supabase
				.from("teacher_grades")
				.select("id")
				.eq("profile_id", user.id)
				.eq("grade_id", gradeId)
				.maybeSingle();

			if (aErr) throw aErr;
			if (!allowed) {
				setError("You don't have access to this grade.");
				setCategories([]);
				return;
			}

			updateSelectedGrade(gradeId);

			const { data: gradeRow, error: gErr } = await supabase
				.from("grades")
				.select("name")
				.eq("id", gradeId)
				.single();

			if (gErr) throw gErr;
			setGradeName(gradeRow?.name || "Your class");

			const isOther = topicId === "other";

			if (!isOther) {
				const { data: tRow, error: tErr } = await supabase
					.from("funmath_topics")
					.select("name")
					.eq("id", topicId)
					.maybeSingle();

				if (tErr) throw tErr;
				setTopicLabel(tRow?.name || "Topic");
			} else {
				setTopicLabel("Other games");
			}

			const { data: cgRows, error: cgErr } = await supabase
				.from("category_grades")
				.select(
					`
					category_id,
					course_categories (
						id,
						name,
						concept,
						description,
						thumbnail,
						status,
						topic_id
					)
				`
				)
				.eq("grade_id", gradeId);

			if (cgErr) throw cgErr;

			const raw = (cgRows || [])
				.map((r) => r.course_categories)
				.filter((c) => c && c.status !== false);

			const filtered = raw.filter((c) => {
				if (isOther) return !c.topic_id;
				return c.topic_id === topicId;
			});

			const byId = new Map();
			filtered.forEach((c) => {
				if (c?.id && !byId.has(c.id)) byId.set(c.id, c);
			});
			setCategories([...byId.values()]);
		} catch (e) {
			console.error(e);
			setError(e.message || "Failed to load games");
		} finally {
			setLoading(false);
		}
	}, [user?.id, gradeId, topicId, supabase, updateSelectedGrade]);

	useEffect(() => {
		load();
	}, [load]);

	const handleGame = (categoryId) => {
		navigate(`/teacher/videos/${gradeId}/${categoryId}`);
	};

	const goTopics = () => navigate(`/teacher/topics/${gradeId}`);
	const goChangeGrade = () => navigate("/dashboard");

	if (loading) {
		return (
			<Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
				<CircularProgress size={36} sx={{ color: "#16a34a" }} />
			</Box>
		);
	}

	return (
		<Box>
			{error && (
				<Alert severity="error" sx={{ mb: 2 }}>
					{error}
				</Alert>
			)}

			<Box sx={{ ...CARD_SURFACE, p: { xs: 2, sm: 2.5 } }}>
				<Box
					sx={{
						display: "flex",
						flexDirection: { xs: "column", sm: "row" },
						alignItems: { xs: "stretch", sm: "center" },
						justifyContent: "space-between",
						gap: { xs: 1.5, sm: 2 },
						mb: 2.5,
						pb: 2,
						borderBottom: "1px solid",
						borderColor: "divider",
					}}>
					<Box
						sx={{
							display: "flex",
							alignItems: "center",
							gap: 0.5,
							minWidth: 0,
						}}>
						<IconButton
							aria-label="Back to topics"
							onClick={goTopics}
							size="small"
							sx={{
								color: "#1e3a5f",
								flexShrink: 0,
								"&:hover": { backgroundColor: "rgba(30, 58, 95, 0.08)" },
							}}>
							<ChevronLeft size={22} strokeWidth={2.5} />
						</IconButton>
						<Box sx={{ minWidth: 0 }}>
							<Typography
								sx={{
									fontWeight: 800,
									color: "#1e3a5f",
									fontSize: { xs: "1.25rem", sm: "1.35rem" },
								}}>
								Choose a game
							</Typography>
							<Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600 }}>
								{topicLabel}
							</Typography>
						</Box>
					</Box>
					<Box
						sx={{
							display: "flex",
							alignItems: "center",
							justifyContent: { xs: "flex-start", sm: "flex-end" },
							gap: 1,
							flexWrap: "wrap",
						}}>
						<Typography
							variant="body2"
							sx={{ color: "text.secondary", fontWeight: 600 }}>
							{gradeName}
						</Typography>
						<Divider
							orientation="vertical"
							flexItem
							sx={{ borderColor: "divider", alignSelf: "stretch", my: 0.25 }}
						/>
						<Link
							component="button"
							type="button"
							variant="body2"
							onClick={goTopics}
							underline="hover"
							sx={{
								cursor: "pointer",
								fontWeight: 600,
								display: "inline-flex",
								alignItems: "center",
								gap: 0.25,
							}}>
							Change topic
							<ChevronRight size={16} />
						</Link>
						<Divider
							orientation="vertical"
							flexItem
							sx={{ borderColor: "divider", alignSelf: "stretch", my: 0.25 }}
						/>
						<Link
							component="button"
							type="button"
							variant="body2"
							onClick={goChangeGrade}
							underline="hover"
							sx={{
								cursor: "pointer",
								fontWeight: 600,
								display: "inline-flex",
								alignItems: "center",
								gap: 0.25,
							}}>
							Change grade
							<ChevronRight size={16} />
						</Link>
					</Box>
				</Box>

				{categories.length === 0 && !error ? (
					<Typography color="text.secondary" align="center">
						No games are available for this topic yet.
					</Typography>
				) : (
					<Grid container spacing={1.5}>
						{categories.map((cat, index) => {
							const { Icon, label } = getTopicVisual(cat);
							const pal =
								TOPIC_TILE_PALETTE[index % TOPIC_TILE_PALETTE.length];
							return (
								<Grid item xs={6} key={cat.id}>
									<Button
										fullWidth
										onClick={() => handleGame(cat.id)}
										sx={{
											py: 1.5,
											px: 1.5,
											borderRadius: 2.5,
											justifyContent: "flex-start",
											alignItems: "center",
											gap: 1.25,
											textTransform: "none",
											color: "#fff",
											backgroundColor: pal.bg,
											boxShadow: `0 5px 0 ${pal.shadow}, 0 8px 20px rgba(0,0,0,0.12)`,
											"&:hover": {
												backgroundColor: pal.bg,
												filter: "brightness(1.06)",
												boxShadow: `0 3px 0 ${pal.shadow}, 0 6px 16px rgba(0,0,0,0.15)`,
												transform: "translateY(2px)",
											},
										}}>
										<Icon
											size={28}
											strokeWidth={2.5}
											style={{ flexShrink: 0 }}
										/>
										<Box
											sx={{
												minWidth: 0,
												flex: 1,
												textAlign: "left",
												display: "flex",
												flexDirection: "column",
												alignItems: "flex-start",
												gap: 0.35,
											}}>
											<Typography
												component="div"
												sx={{
													fontWeight: 800,
													fontSize: "0.95rem",
													lineHeight: 1.25,
													wordBreak: "break-word",
												}}>
												{label}
											</Typography>
											{cat.concept?.trim() ? (
												<Typography
													component="div"
													sx={{
														fontWeight: 600,
														fontSize: "0.78rem",
														lineHeight: 1.35,
														opacity: 0.92,
														wordBreak: "break-word",
													}}>
													{cat.concept.trim()}
												</Typography>
											) : null}
										</Box>
									</Button>
								</Grid>
							);
						})}
					</Grid>
				)}
			</Box>

			<Box
				sx={{
					mt: 3,
					display: "flex",
					flexDirection: "column",
					alignItems: "center",
					gap: 1.25,
					py: 1,
				}}>
				<img
					src="/logo.png"
					alt="Funmiyo"
					style={{ height: 40, width: "auto", objectFit: "contain" }}
				/>
			</Box>
		</Box>
	);
};

export default TeacherGamesByTopic;
